// Shared helpers for outbound partner-attribution links.
// Used by both the place page (to build /api/redirect URLs) and the admin
// place editor (to preview the final tagged URL).

export type PartnerLinkKind = "website" | "reserve" | "tickets";

export const PARTNER_UTM = {
  utm_source: "whatfinland",
  utm_medium: "referral",
  utm_campaign: "platform_mvp",
} as const;

/**
 * Affiliate networks that credit us when their own tracking parameter is
 * present on the outbound link. Matched on hostname (incl. subdomains).
 */
export const AFFILIATE_PARAMS: Array<{
  hosts: string[];
  param: string;
  value: string;
}> = [
  {
    hosts: ["tableonline.fi", "tableonline.com", "tableonline.ee"],
    param: "tlink",
    value: "whatfinland",
  },
];

function matchesHost(hostname: string, host: string): boolean {
  const h = hostname.toLowerCase();
  return h === host || h.endsWith(`.${host}`);
}

/** Case-insensitive lookup: returns the actual key if `name` is present. */
function findParamKey(params: URLSearchParams, name: string): string | null {
  const lower = name.toLowerCase();
  for (const key of params.keys()) {
    if (key.toLowerCase() === lower) return key;
  }
  return null;
}

/** Apply one tag (utm_* or affiliate param) to a set of params, case-insensitively. */
function applyParam(params: URLSearchParams, name: string, value: string): void {
  if (findParamKey(params, name)) return;
  params.set(name, value);
}

/**
 * Some partners paste URLs without a scheme (e.g. `tableonline.fi/fi?x=1`,
 * copied from a browser address bar). `new URL()` throws on those, so prefix
 * `https://` when the string has no scheme but looks like a web URL.
 */
function coerceUrl(rawUrl: string): URL | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const u = new URL(candidate);
    return u.protocol === "http:" || u.protocol === "https:" ? u : null;
  } catch {
    return null;
  }
}

/**
 * SPA-style URLs sometimes keep their query string inside the hash fragment
 * (e.g. `tableonline.fi/fi#/book?x=1`). Append tags to that fragment query
 * too, so the tracking parameter always reaches the partner page.
 */
function tagFragment(u: URL): void {
  if (!u.hash.includes("?")) return;
  const hash = u.hash;
  const qIndex = hash.indexOf("?");
  const before = hash.slice(0, qIndex);
  const queryPart = hash.slice(qIndex + 1);
  const fragParams = new URLSearchParams(queryPart);
  for (const [k, v] of Object.entries(PARTNER_UTM)) applyParam(fragParams, k, v);
  for (const net of AFFILIATE_PARAMS) {
    if (net.hosts.some((h) => matchesHost(u.hostname, h))) {
      applyParam(fragParams, net.param, net.value);
    }
  }
  const next = fragParams.toString();
  if (next) u.hash = `${before}?${next}`;
}

/**
 * Rewrite an outbound URL so it always carries our tracking parameters:
 *  - standard UTM parameters (never overwriting utm_* values the partner set)
 *  - the affiliate tracking parameter when the destination is a known partner
 *    network (e.g. TableOnline's `tlink=whatfinland`)
 * Works even when the link already has query parameters (or a hash-fragment
 * query) and never duplicates an existing tracking parameter, regardless of
 * its letter casing.
 */
export function withUtm(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  const u = coerceUrl(rawUrl);
  if (!u) return rawUrl;

  for (const [k, v] of Object.entries(PARTNER_UTM)) applyParam(u.searchParams, k, v);
  for (const net of AFFILIATE_PARAMS) {
    if (net.hosts.some((h) => matchesHost(u.hostname, h))) {
      applyParam(u.searchParams, net.param, net.value);
    }
  }
  tagFragment(u);

  return u.toString();
}

/**
 * Build the on-site redirect href for an outbound click. The placeRef can be
 * either a slug or a UUID — the server resolves both.
 */
export function partnerHref(placeRef: string, kind: PartnerLinkKind = "website"): string {
  const q = new URLSearchParams({ placeId: placeRef, kind });
  return `/api/redirect?${q.toString()}`;
}

export type LinkRewrite = {
  sourceUrl: string;
  finalUrl: string;
  affiliateNetwork: string | null;
  affiliateApplied: boolean;
  /** True when the partner link already carried the affiliate parameter. */
  alreadyTagged: boolean;
};

/**
 * Describe what the rewriting step did to an outbound link, so the result can
 * be logged and audited (affiliate tracking verification).
 */
export function describeRewrite(rawUrl: string): LinkRewrite {
  const finalUrl = withUtm(rawUrl);
  const u = coerceUrl(rawUrl);
  const net = u
    ? AFFILIATE_PARAMS.find((n) => n.hosts.some((h) => matchesHost(u.hostname, h)))
    : undefined;
  if (!u || !net) {
    return {
      sourceUrl: rawUrl,
      finalUrl,
      affiliateNetwork: null,
      affiliateApplied: false,
      alreadyTagged: false,
    };
  }
  const alreadyTagged = !!findParamKey(u.searchParams, net.param);
  return {
    sourceUrl: rawUrl,
    finalUrl,
    affiliateNetwork: net.hosts[0] ?? null,
    affiliateApplied: true,
    alreadyTagged,
  };
}
